import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Button,
    Typography,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Divider,
    IconButton,
    Collapse,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    AccountTree as TreeIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';

function CurriculumTopicPage() {
    const [topics, setTopics] = useState([]);
    const [relationships, setRelationships] = useState([]);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedParents, setSelectedParents] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [expandedTopics, setExpandedTopics] = useState(new Set());

    const fetchData = useCallback(async () => {
        await Promise.all([fetchTopics(), fetchRelationships()]);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchTopics = async () => {
        const { data, error } = await supabase
            .from('curriculum_topics')
            .select('*')
            .order('name');
        if (error) {
            console.error('Error fetching topics:', error);
        } else {
            setTopics(data || []);
        }
    };

    const fetchRelationships = async () => {
        const { data, error } = await supabase
            .from('curriculum_topics_relationships')
            .select('*');
        if (error) {
            console.error('Error fetching relationships:', error);
        } else {
            setRelationships(data || []);
        }
    };

    const getParentsForTopic = (topicId) => {
        return relationships
            .filter(rel => rel.child_id === topicId)
            .map(rel => topics.find(topic => topic.id === rel.parent_id))
            .filter(Boolean);
    };

    const getChildrenForTopic = (topicId) => {
        return relationships
            .filter(rel => rel.parent_id === topicId)
            .map(rel => topics.find(topic => topic.id === rel.child_id))
            .filter(Boolean);
    };

    const getRootTopics = () => {
        const childIds = new Set(relationships.map(rel => rel.child_id));
        return topics.filter(topic => !childIds.has(topic.id));
    };

    const handleOpen = (topic = null) => {
        if (topic) {
            setName(topic.name);
            setDescription(topic.description || '');
            setEditingId(topic.id);
            const parents = getParentsForTopic(topic.id);
            setSelectedParents(parents.map(p => p.id));
        } else {
            setName('');
            setDescription('');
            setEditingId(null);
            setSelectedParents([]);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setName('');
        setDescription('');
        setEditingId(null);
        setSelectedParents([]);
    };

    const handleSave = async () => {
        try {
            let topicId = editingId;

            if (editingId) {
                // Update existing topic
                await supabase
                    .from('curriculum_topics')
                    .update({ name, description })
                    .eq('id', editingId);

                // Remove existing relationships for this topic
                await supabase
                    .from('curriculum_topics_relationships')
                    .delete()
                    .eq('child_id', editingId);
            } else {
                // Create new topic
                const { data, error } = await supabase
                    .from('curriculum_topics')
                    .insert({ name, description })
                    .select()
                    .single();

                if (error) {
                    console.error('Error creating topic:', error);
                    return;
                }
                topicId = data.id;
            }

            // Create new relationships
            if (selectedParents.length > 0) {
                const relationshipsToInsert = selectedParents.map(parentId => ({
                    parent_id: parentId,
                    child_id: topicId
                }));
                await supabase
                    .from('curriculum_topics_relationships')
                    .insert(relationshipsToInsert);
            }

            handleClose();
            fetchData();
        } catch (error) {
            console.error('Error saving topic:', error);
        }
    };

    const handleDelete = async (topicId) => {
        if (window.confirm('Are you sure you want to delete this topic? This will also remove all its relationships. This action cannot be undone.')) {
            try {
                // First, delete all relationships involving this topic
                await supabase
                    .from('curriculum_topics_relationships')
                    .delete()
                    .or(`parent_id.eq.${topicId},child_id.eq.${topicId}`);

                // Then, delete the topic itself
                const { error } = await supabase
                    .from('curriculum_topics')
                    .delete()
                    .eq('id', topicId);

                if (error) {
                    console.error('Error deleting topic:', error);
                    alert('Error deleting topic. Please try again.');
                } else {
                    // Refresh the data
                    fetchData();
                }
            } catch (error) {
                console.error('Error deleting topic:', error);
                alert('Error deleting topic. Please try again.');
            }
        }
    };

    const toggleExpanded = (topicId) => {
        const newExpanded = new Set(expandedTopics);
        if (newExpanded.has(topicId)) {
            newExpanded.delete(topicId);
        } else {
            newExpanded.add(topicId);
        }
        setExpandedTopics(newExpanded);
    };

    const renderTopicCard = (topic, level = 0) => {
        const children = getChildrenForTopic(topic.id);
        const parents = getParentsForTopic(topic.id);
        const hasChildren = children.length > 0;
        const isExpanded = expandedTopics.has(topic.id);

        return (
            <Box key={topic.id} sx={{ ml: level * 3, mb: 2 }}>
                <Card variant="outlined">
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {hasChildren && (
                                <IconButton
                                    size="small"
                                    onClick={() => toggleExpanded(topic.id)}
                                    sx={{ mr: 1 }}
                                >
                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            )}
                            {!hasChildren && <Box sx={{ width: 40 }} />}
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                {topic.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpen(topic)}
                                    color="primary"
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDelete(topic.id)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {topic.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {topic.description}
                            </Typography>
                        )}

                        {parents.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Parents:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {parents.map(parent => (
                                        <Chip
                                            key={parent.id}
                                            label={parent.name}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {hasChildren && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Children: {children.length}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Collapse in={isExpanded}>
                    <Box sx={{ mt: 1 }}>
                        {children.map(child => renderTopicCard(child, level + 1))}
                    </Box>
                </Collapse>
            </Box>
        );
    };

    const availableParents = topics.filter(topic => topic.id !== editingId);

    return (
        <Container sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TreeIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                    Curriculum Topics Hierarchy
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="body1" color="text.secondary">
                    Manage curriculum topics in a tree structure with multiple parent relationships
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpen()}
                >
                    Create New Topic
                </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {topics.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No topics found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Create your first curriculum topic to get started
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Root Topics
                    </Typography>
                    {getRootTopics().map(topic => renderTopicCard(topic))}
                </Box>
            )}

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingId ? 'Edit Topic' : 'Create New Topic'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Topic Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        margin="dense"
                        required
                    />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        margin="dense"
                        multiline
                        rows={3}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Parent Topics</InputLabel>
                        <Select
                            multiple
                            variant="outlined"
                            value={selectedParents}
                            onChange={(e) => setSelectedParents(e.target.value)}
                            input={<OutlinedInput label="Parent Topics" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const parent = topics.find(t => t.id === value);
                                        return (
                                            <Chip
                                                key={value}
                                                label={parent?.name || 'Unknown'}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {availableParents.map((topic) => (
                                <MenuItem key={topic.id} value={topic.id}>
                                    {topic.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        A topic can have multiple parents. Leave empty for root-level topics.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!name.trim()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default CurriculumTopicPage;